import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
    Card,
    Button,
    Alert,
    Spin,
    Typography,
    Row,
    Col,
    Result,
    Divider,
    Statistic,
    Tag,
    InputNumber,
    Modal,
    Select,
    Space,
    Progress,
} from "antd";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import apiClient from "../../../../helpers/apiClient";
import { useToast } from "../../../../contexts/ToastContext";
import SettingsPageLayout from "../SettingsPageLayout";
import BillingVehicleTable from "./BillingVehicleTable";
import {
    SUBSCRIPTION_INNER_CARD_CLASS,
    SUBSCRIPTION_OUTER_CARD_CLASS,
    subscriptionCardTitle,
} from "./billingLayoutClasses";

const { Text, Paragraph } = Typography;

export const SLOT_RATE_USD = 5;

const getAssignedVehicleIds = (subscription) => {
    if (!subscription) return [];
    return subscription.assignedVehicles ?? [];
};

const calculateSlotTotal = (count) => count * SLOT_RATE_USD;

const getVehicleById = (vehicles, vehicleId) => vehicles?.find((vehicle) => vehicle.id === vehicleId);

const resolveTrialSelectedIds = (summary, syncData) => {
    const fromSync = syncData?.selectedVehicleIds?.filter(Boolean) ?? [];
    if (fromSync.length) return fromSync;
    const fromTrial = summary?.trial?.selectedVehicleIds?.filter(Boolean) ?? [];
    if (fromTrial.length) return fromTrial;
    return (summary?.vehicles ?? []).filter((v) => v.isTrialSelected).map((v) => v.id);
};

const getCheckoutSelectableVehicles = (allVehicles, unsubscribedVehicles) => {
    const eligible = allVehicles.filter((v) => v.subscriptionStatus !== "active");
    return eligible.length ? eligible : unsubscribedVehicles;
};

const getBillingDetails = (summary) => summary?.billingSummary;

const pickSubscriptionSlotFields = (subscription) => {
    if (!subscription) return null;
    return {
        totalSlots: subscription.totalSlots,
        usedSlots: subscription.usedSlots,
        availableSlots: subscription.availableSlots,
        assignedVehicles: subscription.assignedVehicles,
    };
};

const mergeSubscriptionSlotFields = (base, patch) => {
    if (!patch) return base;
    const next = { ...(base ?? {}) };
    if (patch.totalSlots != null) next.totalSlots = patch.totalSlots;
    if (patch.usedSlots != null) next.usedSlots = patch.usedSlots;
    if (patch.availableSlots != null) next.availableSlots = patch.availableSlots;
    if (patch.assignedVehicles != null) next.assignedVehicles = patch.assignedVehicles;
    return Object.keys(next).length ? next : null;
};

const resolveTotalSlots = (subscription) => subscription?.totalSlots ?? 0;

const resolveUsedSlots = (subscription, assignedVehicleIds) => {
    if (subscription?.usedSlots != null) {
        return subscription.usedSlots;
    }
    return assignedVehicleIds.length;
};

const resolveAvailableSlots = (subscription, usedSlots, totalSlots) =>
    subscription?.availableSlots ?? Math.max(0, totalSlots - usedSlots);

const isSubscriptionCurrentlyActive = (subscription, billingSummary) => {
    if (!subscription) return false;
    if (billingSummary?.subscriptionExpired) return false;
    if (billingSummary?.status === "subscription_expired") return false;

    const status = subscription.status || billingSummary?.subscriptionStatus;
    if (status !== "active") return false;

    if (subscription.endDate && new Date(subscription.endDate) <= new Date()) {
        return false;
    }

    return true;
};

const getBillingStatusTagColor = (status) => {
    if (status === "subscribed") return "green";
    if (status === "subscription_expired") return "red";
    return "orange";
};

const resolveInitialSlotCount = (data, mergedIds) => {
    const billingType = data.billingSummary?.type;
    const slotsToBill = data.billingSummary?.slotsToBill;

    if (billingType === "additional_slots") {
        return Math.max(1, slotsToBill ?? 1);
    }

    return Math.max(1, data.billingMode === "trial_only" ? mergedIds.length : 0, slotsToBill ?? 1);
};

const resolveProjectedTotalSlots = ({ subscription, slotCount, hasActiveSubscription, billingDetails }) => {
    const currentTotal = resolveTotalSlots(subscription);

    if (billingDetails?.type === "additional_slots" && typeof subscription?.totalSlots === "number") {
        return subscription.totalSlots;
    }

    if (hasActiveSubscription) {
        return currentTotal + slotCount;
    }

    if (currentTotal > 0) {
        return currentTotal;
    }

    return slotCount;
};

// eslint-disable-next-line no-unused-vars
const SubscriptionBilling = ({ companyData, initialLoading }) => {
    const { t } = useTranslation("settings");
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [billingSummary, setBillingSummary] = useState(null);
    const [subscriptionSlots, setSubscriptionSlots] = useState(null);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [slotCount, setSlotCount] = useState(1);
    const [initialVehicleIds, setInitialVehicleIds] = useState([]);
    const [assigningVehicleId, setAssigningVehicleId] = useState(null);
    const [bulkAssigning, setBulkAssigning] = useState(false);
    const [selectedUnassignedVehicleIds, setSelectedUnassignedVehicleIds] = useState([]);
    const [removeModalOpen, setRemoveModalOpen] = useState(false);
    const [removeTargetVehicleId, setRemoveTargetVehicleId] = useState(null);
    const [swapModalOpen, setSwapModalOpen] = useState(false);
    const [swapTargetVehicleId, setSwapTargetVehicleId] = useState(null);
    const [swapNewVehicleId, setSwapNewVehicleId] = useState(null);
    const [step, setStep] = useState("summary");
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [processingSession, setProcessingSession] = useState(false);
    const [needsReselection, setNeedsReselection] = useState(false);

    const history = useHistory();
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const isSuccess = queryParams.get("success") === "true";
    const isCancelled = queryParams.get("success") === "false";
    const sessionId = queryParams.get("session_id");

    const subscription = billingSummary?.subscription
        ? { ...billingSummary.subscription, ...(subscriptionSlots ?? {}) }
        : null;
    const assignedVehicleIds = getAssignedVehicleIds(subscription);
    const totalSlots = resolveTotalSlots(subscription);
    const usedSlots = resolveUsedSlots(subscription, assignedVehicleIds);
    const availableSlots = resolveAvailableSlots(subscription, usedSlots, totalSlots);
    const unsubscribedVehicles = billingSummary?.unsubscribedVehicles ?? [];
    const allVehicles = billingSummary?.vehicles ?? [];
    const assignedVehicles = assignedVehicleIds
        .map((vehicleId) => getVehicleById(allVehicles, vehicleId))
        .filter(Boolean);
    const paymentRequired = billingSummary?.paymentRequired ?? false;
    const billingSummaryType = billingSummary?.billingSummary?.type;
    const billingMode = billingSummary?.billingMode;
    const billingStateConflict = billingSummary?.billingStateConflict ?? false;
    const isTrialOnly = billingMode === "trial_only";
    const checkoutSelectableVehicles = getCheckoutSelectableVehicles(allVehicles, unsubscribedVehicles);
    const hasActiveSubscription = isSubscriptionCurrentlyActive(subscription, billingSummary);
    const isSubscriptionExpired =
        billingSummary?.subscriptionExpired ||
        billingSummary?.status === "subscription_expired" ||
        Boolean(
            subscription &&
                subscription.endDate &&
                new Date(subscription.endDate) <= new Date() &&
                !hasActiveSubscription,
        );

    const persistVehicleSelection = async (vehicleIds) => {
        if (isTrialOnly) {
            try {
                const res = await apiClient.patch("/api/subscription/trial/sync", { vehicleIds });
                return res.data?.data;
            } catch (err) {
                if (err?.response?.status === 409) {
                    await apiClient.patch("/api/subscription/billing/track-vehicles", { vehicleIds });
                    return null;
                }
                throw err;
            }
        }
        if (vehicleIds.length > 0) {
            await apiClient.patch("/api/subscription/billing/track-vehicles", { vehicleIds });
        }
        return null;
    };

    const fetchBillingSummary = async ({ preserveSlotCount = false } = {}) => {
        setLoading(true);
        try {
            const response = await apiClient.get("/api/subscription/billing/summary");
            const { data } = response.data;
            setBillingSummary(data);
            setSubscriptionSlots(pickSubscriptionSlotFields(data.subscription));

            let mergedIds = [];
            if (data.billingMode === "trial_only") {
                let syncData = null;
                try {
                    const syncResponse = await apiClient.get("/api/subscription/trial/sync");
                    syncData = syncResponse.data?.data;
                } catch (_syncError) {
                    syncData = null;
                }
                mergedIds = resolveTrialSelectedIds(data, syncData);
                setInitialVehicleIds(mergedIds);
                setNeedsReselection(mergedIds.length === 0);
            } else {
                setInitialVehicleIds([]);
                setNeedsReselection(false);
            }

            if (!preserveSlotCount) {
                setSlotCount(resolveInitialSlotCount(data, mergedIds));
            }
        } catch (_error) {
            setError("Unable to fetch subscription details. Please try again later.");
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 800);
        }
    };

    let storedError;
    try {
        storedError = JSON.parse(sessionStorage.getItem("subscriptionError"));
        sessionStorage.removeItem("subscriptionError");
    } catch (_e) {
        storedError = null;
    }

    const message =
        location.state?.message ||
        storedError?.message ||
        (isSubscriptionExpired
            ? "Your subscription has expired. Please renew to continue using the service."
            : "Your trial has expired. Please subscribe to continue using the service.");

    const handleAssignVehicle = async (vehicleId, swapVehicleId = null) => {
        setAssigningVehicleId(vehicleId);
        try {
            const payload = { vehicleId };
            if (swapVehicleId) {
                payload.swapVehicleId = swapVehicleId;
            }

            const assignResponse = await apiClient.patch("/api/subscription/assign-vehicle", payload);
            const assignData = assignResponse.data?.data;
            if (assignData) {
                setSubscriptionSlots((prev) => mergeSubscriptionSlotFields(prev, assignData));
            }
            await fetchBillingSummary({ preserveSlotCount: true });
            if (!swapVehicleId) {
                setSelectedUnassignedVehicleIds((prev) => prev.filter((id) => id !== vehicleId));
            }
            toast.success({ message: t("billing.vehicleAssignedSuccess") });
            setSwapModalOpen(false);
            setSwapTargetVehicleId(null);
            setSwapNewVehicleId(null);
        } catch (err) {
            const msg = err?.response?.data?.message || t("billing.assignFailed");
            if (msg.includes("No available slots")) {
                toast.error({
                    message: t("billing.noFreeSlots"),
                    description: t("billing.purchaseSlotsFirst"),
                });
            } else if (msg.includes("active or upcoming reservation")) {
                toast.error({
                    message: t("billing.swapFailed"),
                    description: t("billing.swapBlockedByReservation"),
                });
            } else if (msg.includes("not active") || msg.includes("expired")) {
                toast.error({
                    message: t("billing.subscriptionInactive"),
                    description: msg,
                });
            } else {
                toast.error({ message: t("billing.assignFailed"), description: msg });
            }
        } finally {
            setAssigningVehicleId(null);
        }
    };

    const handleBulkAssignVehicles = async () => {
        if (selectedUnassignedVehicleIds.length === 0) {
            return;
        }

        setBulkAssigning(true);
        const assignCount = selectedUnassignedVehicleIds.length;
        try {
            const assignResponse = await apiClient.patch("/api/subscription/bulk-assign-vehicles", {
                vehicleIds: selectedUnassignedVehicleIds,
            });
            const assignData = assignResponse.data?.data;
            if (assignData) {
                setSubscriptionSlots((prev) => mergeSubscriptionSlotFields(prev, assignData));
            }
            await fetchBillingSummary({ preserveSlotCount: true });
            setSelectedUnassignedVehicleIds([]);
            toast.success({
                message: t("billing.bulkAssignSuccess", { count: assignCount }),
            });
        } catch (err) {
            const msg = err?.response?.data?.message || t("billing.assignFailed");
            if (msg.includes("No available slots")) {
                toast.error({
                    message: t("billing.noFreeSlots"),
                    description: t("billing.purchaseSlotsFirst"),
                });
            } else if (msg.includes("not active") || msg.includes("expired")) {
                toast.error({
                    message: t("billing.subscriptionInactive"),
                    description: msg,
                });
            } else {
                toast.error({ message: t("billing.assignFailed"), description: msg });
            }
        } finally {
            setBulkAssigning(false);
        }
    };

    const handleRemoveVehicleFromSlot = async (vehicleId) => {
        setAssigningVehicleId(vehicleId);
        try {
            const removeResponse = await apiClient.patch("/api/subscription/remove-vehicle-from-slot", {
                vehicleId,
            });
            const removeData = removeResponse.data?.data;
            if (removeData) {
                setSubscriptionSlots((prev) => mergeSubscriptionSlotFields(prev, removeData));
            }
            await fetchBillingSummary({ preserveSlotCount: true });
            toast.success({ message: t("billing.vehicleRemovedSuccess") });
            setRemoveModalOpen(false);
            setRemoveTargetVehicleId(null);
        } catch (err) {
            const msg = err?.response?.data?.message || t("billing.removeFailed");
            if (msg.includes("active or upcoming reservation")) {
                toast.error({
                    message: t("billing.removeFailed"),
                    description: t("billing.removeBlockedByReservation"),
                });
            } else if (msg.includes("not active") || msg.includes("expired")) {
                toast.error({
                    message: t("billing.subscriptionInactive"),
                    description: msg,
                });
            } else {
                toast.error({ message: t("billing.removeFailed"), description: msg });
            }
        } finally {
            setAssigningVehicleId(null);
        }
    };

    const handleTrackVehicles = async () => {
        if (billingStateConflict) {
            setError(t("billing.checkoutConflict"));
            return;
        }

        if (initialVehicleIds.length > slotCount) {
            setError(t("billing.tooManyVehiclesForSlots"));
            return;
        }

        setLoading(true);
        try {
            const syncResult = await persistVehicleSelection(initialVehicleIds);
            if (syncResult?.selectedVehicles) {
                setInitialVehicleIds(syncResult.selectedVehicles.map((v) => v.id));
            }
            setNeedsReselection(false);
            setStep("review");
        } catch (_error) {
            setError(t("billing.syncFailed"));
        } finally {
            setLoading(false);
        }
    };

    const handleCheckout = async () => {
        if (billingStateConflict) {
            setError(t("billing.checkoutConflict"));
            return;
        }

        if (initialVehicleIds.length > slotCount) {
            setError(t("billing.tooManyVehiclesForSlots"));
            return;
        }

        setProcessingPayment(true);
        try {
            const payload = {
                slotCount,
            };

            if (initialVehicleIds.length > 0) {
                payload.initialVehicleIds = initialVehicleIds;
            }

            sessionStorage.setItem("billingCheckoutSlotCount", String(slotCount));

            const response = await apiClient.post("/api/subscription/billing/checkout", payload);

            if (response.data.data.url) {
                window.location.href = response.data.data.url;
            } else if (response.data.data.sessionId) {
                window.location.href = response.data.data.sessionUrl;
            }
        } catch (err) {
            if (err?.response?.status === 409) {
                setError(err.response.data?.message || t("billing.checkoutConflict"));
            } else {
                setError("Unable to process payment. Please try again later.");
            }
            setProcessingPayment(false);
        }
    };

    const handleCancel = () => {
        history.push("/login");
    };

    const handleGoToDashboard = () => {
        history.push("/settings?menu=companyInfo");
    };

    const handleBuySlots = (count = 1) => {
        if (billingStateConflict) {
            setError(t("billing.checkoutConflict"));
            return;
        }
        setSlotCount(Math.max(1, count));
        setInitialVehicleIds([]);
        setStep("review");
    };

    const openRemoveModal = (vehicleId) => {
        setRemoveTargetVehicleId(vehicleId);
        setRemoveModalOpen(true);
    };

    const openSwapModal = (vehicleId) => {
        setSwapTargetVehicleId(vehicleId);
        setSwapNewVehicleId(null);
        setSwapModalOpen(true);
    };

    const handleUnassignedVehicleSelection = (selectedRowKeys) => {
        const limitedSelection = selectedRowKeys.slice(0, availableSlots);
        setSelectedUnassignedVehicleIds(limitedSelection);
    };

    const handleInitialVehicleSelection = (selectedRowKeys) => {
        const limitedSelection = selectedRowKeys.slice(0, slotCount);
        setInitialVehicleIds(limitedSelection);
        if (limitedSelection.length > 0) {
            setNeedsReselection(false);
        }
    };

    const handleSlotCountChange = (value) => {
        const nextCount = Math.max(1, value || 1);
        setSlotCount(nextCount);
        if (initialVehicleIds.length > nextCount) {
            setInitialVehicleIds(initialVehicleIds.slice(0, nextCount));
        }
    };

    useEffect(() => {
        if (sessionStorage.getItem("isRedirectingToBilling")) {
            sessionStorage.removeItem("isRedirectingToBilling");
        }

        sessionStorage.setItem("onBillingPage", "true");
        sessionStorage.removeItem("redirectedForBilling");

        if (isSuccess && sessionId) {
            history.replace("/settings?menu=billing");

            toast.success({
                message: t("billing.checkoutPaymentSuccessful"),
                description: t("billing.checkoutPaymentSuccessfulDescription"),
                duration: 6,
            });

            setProcessingSession(false);
            setStep("success");
            setPaymentSuccess(true);
            fetchBillingSummary();
        } else if (isCancelled) {
            history.replace("/settings?menu=billing");

            toast.warning({
                message: t("billing.checkoutPaymentCancelled"),
                description: t("billing.checkoutPaymentCancelledDescription"),
                duration: 6,
            });

            fetchBillingSummary();
        } else {
            fetchBillingSummary();
        }

        return () => {
            sessionStorage.removeItem("onBillingPage");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, isSuccess, isCancelled]);

    const renderWithLayout = (content) => (
        <SettingsPageLayout title={t("billing.title")} description={t("billing.description")}>
            {content}
        </SettingsPageLayout>
    );

    if (loading || processingSession) {
        return renderWithLayout(
            <div style={{ textAlign: "center", padding: "50px" }}>
                <Spin size="large" />
                <p>
                    {processingSession
                        ? "Processing your subscription payment..."
                        : "Preparing your subscription details..."}
                </p>
            </div>,
        );
    }

    if (!billingSummary && loading) {
        return renderWithLayout(
            <div style={{ textAlign: "center", padding: "50px" }}>
                <Alert
                    message="Error"
                    description="Failed to load subscription details. Please refresh the page or contact support."
                    type="error"
                    showIcon
                />
                <Button type="primary" style={{ marginTop: 16 }} onClick={() => window.location.reload()}>
                    Refresh Page
                </Button>
            </div>,
        );
    }

    if (step === "success" || paymentSuccess) {
        const successSubscription = billingSummary?.subscription
            ? { ...billingSummary.subscription, ...(subscriptionSlots ?? {}) }
            : null;
        const successAssignedIds = getAssignedVehicleIds(successSubscription);
        const checkoutSlotCount = Number(sessionStorage.getItem("billingCheckoutSlotCount")) || 0;
        const purchasedSlots = resolveTotalSlots(successSubscription) || checkoutSlotCount || slotCount;
        sessionStorage.removeItem("billingCheckoutSlotCount");
        const assignedCount = resolveUsedSlots(successSubscription, successAssignedIds);

        return renderWithLayout(
            <Card
                title={subscriptionCardTitle("Subscription Activated")}
                className={`${SUBSCRIPTION_OUTER_CARD_CLASS} mx-auto !max-w-[900px]`}>
                <Result
                    status="success"
                    title="Payment Successful!"
                    subTitle="Your subscription has been activated successfully."
                    extra={[
                        <Button type="primary" key="dashboard" onClick={handleGoToDashboard}>
                            Go to Dashboard
                        </Button>,
                    ]}
                />

                <Card
                    type="inner"
                    title="Subscription Details"
                    className={`${SUBSCRIPTION_INNER_CARD_CLASS} mb-6 mt-6`}
                    style={{ marginBottom: 24, marginTop: 24 }}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                            <Text strong>Company: </Text>
                            <Text>{billingSummary?.companyName || "N/A"}</Text>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Text strong>Status: </Text>
                            <Tag color="green">ACTIVE</Tag>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Text strong>Slots Purchased: </Text>
                            <Text>{purchasedSlots}</Text>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Text strong>Assigned Vehicles: </Text>
                            <Text>
                                {assignedCount} / {purchasedSlots}
                            </Text>
                        </Col>
                        <Col span={24}>
                            <Text strong>Monthly Amount: </Text>
                            <Text>${calculateSlotTotal(purchasedSlots).toFixed(2)}</Text>
                        </Col>
                    </Row>
                </Card>

                <Alert
                    message="Next Steps"
                    description="You can now return to the dashboard and continue using all features of the platform. Assign vehicles to your slots from the billing page at any time."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            </Card>,
        );
    }

    if (step === "summary") {
        const needsCheckoutFlow =
            !billingStateConflict &&
            !hasActiveSubscription &&
            (paymentRequired ||
                billingSummaryType === "trial_with_vehicles" ||
                billingSummaryType === "monthly_renewal" ||
                isSubscriptionExpired ||
                (isTrialOnly && billingSummary?.status === "trial_expired"));
        const slotUsagePercent = totalSlots > 0 ? Math.round((usedSlots / totalSlots) * 100) : 0;

        return renderWithLayout(
            <Card
                title={subscriptionCardTitle("Subscription Management")}
                className={SUBSCRIPTION_OUTER_CARD_CLASS}>
                {error && (
                    <Alert
                        message="Error"
                        description={error}
                        type="error"
                        style={{ marginBottom: 16 }}
                        showIcon
                        closable
                        onClose={() => setError(null)}
                    />
                )}

                {billingSummary?.status === "trial_expired" && (
                    <Result
                        status="warning"
                        title="Trial Expired"
                        subTitle={message}
                        style={{ padding: "12px 0 24px" }}
                    />
                )}

                {isSubscriptionExpired && billingSummary?.status !== "trial_expired" && (
                    <Result
                        status="warning"
                        title="Subscription Expired"
                        subTitle={message}
                        style={{ padding: "12px 0 24px" }}
                    />
                )}

                {billingStateConflict && (
                    <Alert
                        message={t("billing.billingStateConflict")}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                {isTrialOnly && needsReselection && !billingStateConflict && (
                    <Alert
                        message={t("billing.reselectVehiclesAfterTrialExtend")}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                {billingSummary && (
                    <>
                        <Card
                            type="inner"
                            title="Company Details"
                            className={`${SUBSCRIPTION_INNER_CARD_CLASS} mb-4`}
                            style={{ marginBottom: 16 }}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={12}>
                                    <Text strong>Company: </Text>
                                    <Text className="break-words">{billingSummary.companyName || "N/A"}</Text>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Text strong>Status: </Text>
                                    <Tag color={getBillingStatusTagColor(billingSummary.status)}>
                                        {(billingSummary.status || "").replace(/_/g, " ").toUpperCase()}
                                    </Tag>
                                </Col>
                                {billingSummary.trial && (
                                    <Col span={24}>
                                        <Text strong>Trial Period: </Text>
                                        <Text>
                                            {billingSummary.trial.trialStartDate
                                                ? new Date(billingSummary.trial.trialStartDate).toLocaleDateString()
                                                : "Not specified"}{" "}
                                            to {new Date(billingSummary.trial.trialEndDate).toLocaleDateString()}
                                            {billingSummary.trial.daysRemaining > 0 && (
                                                <span> ({billingSummary.trial.daysRemaining} days remaining)</span>
                                            )}
                                        </Text>
                                    </Col>
                                )}
                                {subscription && (
                                    <Col span={24}>
                                        <Text strong>Current Subscription: </Text>
                                        <Text>
                                            {hasActiveSubscription
                                                ? `${totalSlots} slots purchased`
                                                : `${totalSlots} slots from previous period`}
                                            {subscription.endDate &&
                                                (hasActiveSubscription
                                                    ? `, Next billing date: ${new Date(subscription.endDate).toLocaleDateString()}`
                                                    : `, Expired on: ${new Date(subscription.endDate).toLocaleDateString()}`)}
                                        </Text>
                                    </Col>
                                )}
                            </Row>
                        </Card>

                        {hasActiveSubscription && (
                            <Card
                                type="inner"
                                title="Slot Usage"
                                className={`${SUBSCRIPTION_INNER_CARD_CLASS} mb-4`}
                                style={{ marginBottom: 16 }}>
                                <Row gutter={[16, 16]} align="middle">
                                    <Col span={24}>
                                        <Text className="text-sm sm:text-base">
                                            {t("billing.slotsUsed", { used: usedSlots, total: totalSlots })}
                                            {" · "}
                                            {t("billing.availableSlots", { count: availableSlots })}
                                        </Text>
                                        <Progress
                                            percent={slotUsagePercent}
                                            status={availableSlots === 0 ? "exception" : "active"}
                                            style={{ marginTop: 8 }}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        )}

                        {hasActiveSubscription && (
                            <>
                                <Card
                                    type="inner"
                                    title={`Assigned Vehicles (${assignedVehicles.length})`}
                                    className={`${SUBSCRIPTION_INNER_CARD_CLASS} mb-4`}
                                    style={{ marginBottom: 16 }}>
                                    {assignedVehicles.length > 0 ? (
                                        <BillingVehicleTable
                                            dataSource={assignedVehicles}
                                            renderActions={(record) => (
                                                <Space size="small" wrap>
                                                    {unsubscribedVehicles.length > 0 && (
                                                        <Button
                                                            size="small"
                                                            className="!h-9"
                                                            onClick={() => openSwapModal(record.id)}
                                                            loading={assigningVehicleId === record.id}>
                                                            {t("billing.swapVehicle")}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="small"
                                                        danger
                                                        className="!h-9"
                                                        onClick={() => openRemoveModal(record.id)}
                                                        loading={assigningVehicleId === record.id}>
                                                        {t("billing.removeFromSlot")}
                                                    </Button>
                                                </Space>
                                            )}
                                        />
                                    ) : (
                                        <Paragraph type="secondary">
                                            No vehicles assigned yet. Assign eligible vehicles below.
                                        </Paragraph>
                                    )}
                                </Card>

                                {unsubscribedVehicles.length > 0 && (
                                    <Card
                                        type="inner"
                                        title={`Unassigned Vehicles (${unsubscribedVehicles.length})`}
                                        className={`${SUBSCRIPTION_INNER_CARD_CLASS} mb-4 [&_.ant-card-head]:!flex-wrap [&_.ant-card-head]:!gap-2 [&_.ant-card-extra]:!ml-0 [&_.ant-card-extra]:!w-full sm:[&_.ant-card-extra]:!ml-auto sm:[&_.ant-card-extra]:!w-auto`}
                                        style={{ marginBottom: 16 }}
                                        extra={
                                            availableSlots > 0 ? (
                                                <Button
                                                    type="primary"
                                                    className="!h-10 w-full sm:!w-auto"
                                                    onClick={handleBulkAssignVehicles}
                                                    disabled={selectedUnassignedVehicleIds.length === 0}
                                                    loading={bulkAssigning}>
                                                    {t("billing.bulkAssignVehicles", {
                                                        count: selectedUnassignedVehicleIds.length,
                                                    })}
                                                </Button>
                                            ) : null
                                        }>
                                        <BillingVehicleTable
                                            dataSource={unsubscribedVehicles}
                                            size="large"
                                            tableStyles={{
                                                body: {
                                                    cell: {
                                                        paddingTop: 16,
                                                        paddingBottom: 16,
                                                    },
                                                },
                                            }}
                                            rowSelection={
                                                availableSlots > 0
                                                    ? {
                                                          selectedRowKeys: selectedUnassignedVehicleIds,
                                                          onChange: handleUnassignedVehicleSelection,
                                                          getCheckboxProps: () => ({
                                                              disabled: bulkAssigning,
                                                          }),
                                                      }
                                                    : undefined
                                            }
                                            renderActions={
                                                availableSlots > 0
                                                    ? (record) => (
                                                          <Button
                                                              type="primary"
                                                              size="small"
                                                              className="!h-9"
                                                              onClick={() => handleAssignVehicle(record.id)}
                                                              loading={assigningVehicleId === record.id}>
                                                              {t("billing.assignVehicle")}
                                                          </Button>
                                                      )
                                                    : undefined
                                            }
                                        />
                                        {availableSlots > 0 && (
                                            <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
                                                {t("billing.bulkAssignHint", { count: availableSlots })}
                                            </Paragraph>
                                        )}
                                    </Card>
                                )}

                                <Card
                                    type="inner"
                                    title={t("billing.buySlots")}
                                    className={`${SUBSCRIPTION_INNER_CARD_CLASS} mb-6`}
                                    style={{ marginBottom: 24 }}>
                                    <Row gutter={[16, 16]} align="middle">
                                        <Col xs={24} sm={12} md={8}>
                                            <Text>{t("billing.slotRate")}</Text>
                                        </Col>
                                        <Col xs={24} sm={12} md={8}>
                                            <InputNumber
                                                className="!w-full max-w-xs"
                                                min={1}
                                                value={slotCount}
                                                onChange={handleSlotCountChange}
                                                addonBefore={
                                                    <Button
                                                        type="text"
                                                        icon={<MinusOutlined />}
                                                        onClick={() => handleSlotCountChange(slotCount - 1)}
                                                    />
                                                }
                                                addonAfter={
                                                    <Button
                                                        type="text"
                                                        icon={<PlusOutlined />}
                                                        onClick={() => handleSlotCountChange(slotCount + 1)}
                                                    />
                                                }
                                            />
                                        </Col>
                                        <Col xs={24} sm={12} md={8}>
                                            <Text strong>${calculateSlotTotal(slotCount).toFixed(2)}/mo</Text>
                                        </Col>
                                        <Col span={24}>
                                            <Text type="secondary" className="text-sm">
                                                {t("billing.totalSlotsSummary", {
                                                    total: resolveProjectedTotalSlots({
                                                        subscription,
                                                        slotCount,
                                                        hasActiveSubscription: true,
                                                        billingDetails: getBillingDetails(billingSummary),
                                                    }),
                                                    additional: slotCount,
                                                })}
                                            </Text>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Button
                                                type="primary"
                                                className="!h-11 w-full sm:!w-auto"
                                                onClick={() => handleBuySlots(slotCount)}
                                                disabled={billingStateConflict}>
                                                {t("billing.buySlots")}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card>

                                {availableSlots === 0 && unsubscribedVehicles.length > 0 && (
                                    <Alert
                                        message="No free slots available"
                                        description="Purchase additional slots to assign more vehicles."
                                        type="warning"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                        action={
                                            <Button
                                                size="small"
                                                onClick={() => handleBuySlots(1)}
                                                disabled={billingStateConflict}>
                                                {t("billing.buySlots")}
                                            </Button>
                                        }
                                    />
                                )}

                                <Row justify="end" style={{ marginBottom: 24 }}>
                                    <Col xs={24} sm={12} md={8} lg={6}>
                                        <Button
                                            type="primary"
                                            className="!h-11 w-full sm:!w-auto"
                                            onClick={() => history.push("/")}>
                                            Continue
                                        </Button>
                                    </Col>
                                </Row>
                            </>
                        )}

                        {needsCheckoutFlow && (
                            <>
                                <Paragraph>
                                    Purchase subscription slots at a flat rate. You can optionally assign vehicles
                                    immediately after checkout.
                                </Paragraph>

                                <Card type="inner" title="Purchase Slots" style={{ marginBottom: 16 }}>
                                    <Row gutter={[16, 16]} align="middle">
                                        <Col>
                                            <Text strong>Number of slots: </Text>
                                        </Col>
                                        <Col>
                                            <Button
                                                icon={<MinusOutlined />}
                                                onClick={() => handleSlotCountChange(slotCount - 1)}
                                                disabled={slotCount <= 1}
                                            />
                                        </Col>
                                        <Col>
                                            <InputNumber min={1} value={slotCount} onChange={handleSlotCountChange} />
                                        </Col>
                                        <Col>
                                            <Button
                                                icon={<PlusOutlined />}
                                                onClick={() => handleSlotCountChange(slotCount + 1)}
                                            />
                                        </Col>
                                        <Col span={24}>
                                            <Statistic
                                                title="Monthly Total"
                                                value={calculateSlotTotal(slotCount)}
                                                precision={2}
                                                prefix="$"
                                                suffix="/ month"
                                            />
                                            <Text type="secondary">{t("billing.slotRate")}</Text>
                                        </Col>
                                    </Row>
                                </Card>

                                {checkoutSelectableVehicles.length > 0 && (
                                    <Card
                                        type="inner"
                                        title={`Assign Vehicles Now (optional, max ${slotCount})`}
                                        style={{ marginBottom: 24 }}>
                                        <Paragraph type="secondary">
                                            Select up to {slotCount} vehicle(s) to assign immediately after payment.
                                        </Paragraph>
                                        <BillingVehicleTable
                                            dataSource={checkoutSelectableVehicles}
                                            rowSelection={{
                                                selectedRowKeys: initialVehicleIds,
                                                onChange: handleInitialVehicleSelection,
                                                getCheckboxProps: () => ({
                                                    disabled: false,
                                                }),
                                            }}
                                        />
                                        <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
                                            {t("billing.vehiclesSelectedForSubscription", {
                                                count: initialVehicleIds.length,
                                            })}
                                        </Paragraph>
                                    </Card>
                                )}

                                <Row gutter={[12, 12]} justify="space-between" style={{ marginBottom: 24 }}>
                                    <Col xs={24} sm={12} md={8}>
                                        <Button
                                            type="default"
                                            className="!h-11 w-full sm:!w-auto"
                                            onClick={handleCancel}
                                            danger>
                                            Cancel
                                        </Button>
                                    </Col>
                                    <Col xs={24} sm={12} md={10}>
                                        <Button
                                            type="primary"
                                            className="!h-11 w-full sm:!w-auto"
                                            onClick={handleTrackVehicles}
                                            disabled={slotCount < 1 || billingStateConflict}>
                                            Review Order ({slotCount} slot{slotCount !== 1 ? "s" : ""})
                                        </Button>
                                    </Col>
                                </Row>
                            </>
                        )}

                        {!hasActiveSubscription && !needsCheckoutFlow && (
                            <Card type="inner" title="No Payment Required" style={{ marginBottom: 24 }}>
                                <Paragraph>
                                    Your current subscription plan does not require any payment. You can continue using
                                    the service without any changes.
                                </Paragraph>
                                <Row justify="end">
                                    <Button type="primary" onClick={() => history.push("/")}>
                                        Continue
                                    </Button>
                                </Row>
                            </Card>
                        )}
                    </>
                )}

                <Modal
                    title={t("billing.removeFromSlot")}
                    open={removeModalOpen}
                    centered
                    classNames={{
                        content:
                            "!w-[calc(100vw-2rem)] !max-w-[calc(100vw-2rem)] sm:!w-[480px] sm:!max-w-[480px]",
                    }}
                    onCancel={() => {
                        setRemoveModalOpen(false);
                        setRemoveTargetVehicleId(null);
                    }}
                    onOk={() => {
                        if (removeTargetVehicleId) {
                            handleRemoveVehicleFromSlot(removeTargetVehicleId);
                        }
                    }}
                    okButtonProps={{
                        danger: true,
                        loading: Boolean(assigningVehicleId),
                    }}>
                    <Paragraph>{t("billing.removeFromSlotConfirm")}</Paragraph>
                </Modal>

                <Modal
                    title={t("billing.swapVehicle")}
                    open={swapModalOpen}
                    centered
                    classNames={{
                        content:
                            "!w-[calc(100vw-2rem)] !max-w-[calc(100vw-2rem)] sm:!w-[480px] sm:!max-w-[480px]",
                    }}
                    onCancel={() => {
                        setSwapModalOpen(false);
                        setSwapTargetVehicleId(null);
                        setSwapNewVehicleId(null);
                    }}
                    onOk={() => {
                        if (swapTargetVehicleId && swapNewVehicleId) {
                            handleAssignVehicle(swapNewVehicleId, swapTargetVehicleId);
                        }
                    }}
                    okButtonProps={{
                        disabled: !swapNewVehicleId,
                        loading: Boolean(assigningVehicleId),
                    }}>
                    <Paragraph>{t("billing.swapVehiclePrompt")}</Paragraph>
                    <Select
                        className="!w-full"
                        style={{ width: "100%" }}
                        placeholder={t("billing.selectSwapVehicle")}
                        value={swapNewVehicleId}
                        onChange={setSwapNewVehicleId}
                        options={unsubscribedVehicles.map((vehicle) => ({
                            value: vehicle.id,
                            label: `${vehicle.name} (${vehicle.plate || vehicle.vin})`,
                        }))}
                    />
                </Modal>

                <Alert
                    message="Need Help?"
                    description="If you have any issues with your subscription, please contact our customer service team at support@softcartech.com."
                    type="info"
                    showIcon
                />
            </Card>,
        );
    }

    if (step === "review") {
        const isBuyingAdditionalSlots = billingSummaryType === "additional_slots" || hasActiveSubscription;
        const projectedTotalSlots = resolveProjectedTotalSlots({
            subscription,
            slotCount,
            hasActiveSubscription,
            billingDetails: getBillingDetails(billingSummary),
        });

        return renderWithLayout(
            <Card
                title={subscriptionCardTitle("Subscription Checkout")}
                className={`${SUBSCRIPTION_OUTER_CARD_CLASS} mx-auto !max-w-[900px]`}>
                {error && (
                    <Alert
                        message="Error"
                        description={error}
                        type="error"
                        style={{ marginBottom: 16 }}
                        showIcon
                        closable
                        onClose={() => setError(null)}
                    />
                )}

                {billingStateConflict && (
                    <Alert
                        message={t("billing.billingStateConflict")}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Card
                    type="inner"
                    title="Subscription Summary"
                    className={`${SUBSCRIPTION_INNER_CARD_CLASS} mb-6`}
                    style={{ marginBottom: 24 }}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                            <Text strong>Company: </Text>
                            <Text>{billingSummary.companyName || "N/A"}</Text>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Text strong>Billing Type: </Text>
                            <Text>{(billingSummaryType || "subscription").replace(/_/g, " ")}</Text>
                        </Col>
                    </Row>

                    <Divider />

                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                            <Text strong>
                                {isBuyingAdditionalSlots
                                    ? t("billing.additionalSlotsToPurchase")
                                    : t("billing.slotsPurchased")}
                                :{" "}
                            </Text>
                            <Text>{slotCount}</Text>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Text strong>Rate per Slot: </Text>
                            <Text>${SLOT_RATE_USD.toFixed(2)} / month</Text>
                        </Col>
                        {isBuyingAdditionalSlots && (
                            <Col span={24}>
                                <Text strong>{t("billing.totalSlotsAfterPurchase")}: </Text>
                                <Text>
                                    {t("billing.totalSlotsSummary", {
                                        total: projectedTotalSlots,
                                        additional: slotCount,
                                    })}
                                </Text>
                            </Col>
                        )}
                        {initialVehicleIds.length > 0 && (
                            <Col span={24}>
                                <Text strong>Vehicles to Assign: </Text>
                                <Text>{initialVehicleIds.length}</Text>
                            </Col>
                        )}
                    </Row>

                    <Divider />

                    <Row>
                        <Col xs={24} md={{ span: 12, offset: 12 }}>
                            <Statistic
                                title="Total Amount"
                                value={calculateSlotTotal(slotCount)}
                                precision={2}
                                prefix="$"
                                suffix="/ month"
                                valueStyle={{ color: "#3f8600", fontWeight: "bold" }}
                            />
                            <Text type="secondary">Billed monthly</Text>
                        </Col>
                    </Row>
                </Card>

                <Card type="inner" title="Payment Information" style={{ marginBottom: 24 }}>
                    <Paragraph>
                        By clicking &quot;Proceed to Payment&quot;, you will be redirected to our secure payment
                        processor to complete your subscription.
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <li>Your subscription will start immediately upon successful payment</li>
                            <li>You will be billed monthly on this date going forward</li>
                            <li>You can assign vehicles to slots or purchase more slots at any time</li>
                        </ul>
                    </Paragraph>
                </Card>

                <Row gutter={[12, 12]} justify="space-between">
                    <Col xs={24} sm={12} md={8}>
                        <Button className="!h-11 w-full sm:!w-auto" onClick={() => setStep("summary")}>
                            Back
                        </Button>
                    </Col>
                    <Col xs={24} sm={12} md={10}>
                        <Button
                            type="primary"
                            className="!h-11 w-full sm:!w-auto"
                            onClick={handleCheckout}
                            loading={processingPayment}
                            disabled={billingStateConflict}
                            size="large">
                            Proceed to Payment
                        </Button>
                    </Col>
                </Row>

                <div style={{ marginTop: 24 }}>
                    <Alert
                        message="Secure Transaction"
                        description="All payments are processed securely through Stripe. Your payment information is never stored on our servers."
                        type="info"
                        showIcon
                    />
                </div>
            </Card>,
        );
    }

    return null;
};

SubscriptionBilling.propTypes = {
    companyData: PropTypes.object,
    initialLoading: PropTypes.bool,
};

export default SubscriptionBilling;
